import React, { useState, useEffect } from "react";
import { generateXRayChestReport } from "../../utils/xraychest";
import { API_BASE_URL } from "../Api/apiconnector";

export default function XRayChestForm({ onSubmit , currentUser, onClose, onBack }) {
  const initial = {
    name: "",
    patientId: "",
    age: "",
    testDate: "",
    reportDate: "",
    gender: "",
    xrayType: "",

    chestNormal: false,

    // Opacities and Mild haziness: type + zones
    opacities: {
      enabled: false,
      type: "", // "Homogeneous", "Ill-defined", "Non homogeneous"
      zones: {
        RUZ: false,
        RMZ: false,
        RLZ: false,
        LUZ: false,
        LMZ: false,
        LLZ: false,
      },
      zoneFindings: {},
    },

    mildHaziness: {
      enabled: false,
      zones: {
        RUZ: false,
        RMZ: false,
        RLZ: false,
        LUZ: false,
        LMZ: false,
        LLZ: false,
      },
      additional: "",
    },

    rightHilarShadow: "", // "prominent" | "calcifications"
    rightHilarImpression: "",
    leftHilarShadow: "",
    leftHilarImpression: "",

    rightCostophrenicAngle: "", // "obliterated"
    leftCostophrenicAngle: "",
    rightPleuralEffusion: false,
    leftPleuralEffusion: false,

    pneumothorax: false,
    pneumothoraxSide: "", // "right" | "left"
    pneumothoraxSeverity: { right: "", left: "" }, // mild/moderate/severe

    cardiacSilhouette: "", // "enlarged" | "cannot be commented"
    cardiacDetails: "", // CTR & Additional findings when enlarged

    softTissue: false,
    softTissueFindings: "",

    bonyThoracicCage: false,
    bony: {
      ribFracture: false,
      ribSide: "", // right|left
      ribNumbers: "", // free text / numbers
      clavicleFracture: false,
      clavicleSide: "", // right|left
      clavicleLocation: "", // proximal/middle/distal
      sternalSutures: false,
      bonyAdditional: "",
    },

    tracheaMediastinal: "", // "shifted"
    tracheaShiftSide: "", // right|left
    tracheaImpression: "",

    domesOfDiaphragm: false,
    diaphragmSide: "", // right|left
    diaphragmState: "", // Elevated|Flattened
    diaphragmElevatedReason: "", // Eventration | Abdominal Distention

    etLine: false,
    cvpLine: false,
    tracheostomy: false,
    ngTube: false,

    additionalFindings: "",
  };

  const [formData, setFormData] = useState(initial);

  const zones = ["RUZ", "RMZ", "RLZ", "LUZ", "LMZ", "LLZ"];

   useEffect(() => {
    const fetchDemographics = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const patientId = urlParams.get("id");
        if (!patientId) return;

        const response = await fetch(`${API_BASE_URL}/fetch-dicom/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ id: patientId }),
        });

        if (!response.ok) throw new Error(`Failed to fetch demographics (${response.status})`);
        const data = await response.json();
        console.log("✅ Fetched demographics:", data);

        const formattedStudyDate = data.study_date || "";
        const now = new Date();
        const formattedReportDate = formatToDDMMYYYY(now);
        const formattedReportTime = now.toLocaleTimeString("en-GB");

        setFormData((prev) => ({
          ...prev,
          name: data.patient_name || "",
          patientId: data.patient_id || "",
          age: data.age || "",
          gender: data.gender || "",
          referralDoctor: data.referring_doctor_name || "",
          testDate: formattedStudyDate,
          reportDate: formattedReportDate,
          reportTime: formattedReportTime,
        }));
      } catch (error) {
        console.error("❌ Error fetching DICOM demographics:", error);
      }
    };

    fetchDemographics();
  }, []);

  // ✅ Helpers
  function formatToDDMMYYYY(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  function formatToISODate(ddmmyyyy) {
    if (!ddmmyyyy) return "";
    const [day, month, year] = ddmmyyyy.split("-");
    return `${year}-${month}-${day}`;
  }

  function formatToDDMMYYYYFromISO(isoDate) {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}-${month}-${year}`;
  }

  const setField = (path, value) => {
    if (!path.includes(".")) {
      setFormData((prev) => ({ ...prev, [path]: value }));
      return;
    }
    const parts = path.split(".");
    setFormData((prev) => {
      const copy = { ...prev };
      let cur = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...cur[parts[i]] };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return copy;
    });
  };

  

  const toggleZone = (group, zone) => {
    setFormData(prev => ({
      ...prev,
      [group]: {
        ...prev[group],
        zones: { ...prev[group].zones, [zone]: !prev[group].zones[zone] }
      }
    }));
  };
   
  const handleSubmit = (formData) => {
    const report = generateXRayChestReport(formData, currentUser);
    // Do something with the report (display, save, etc.)
    console.log(report);
  };

  const transformFormDataForReport = (formData) => {
    const transformed = {
      // Basic info
      name: formData.name,
      patientId: formData.patientId,
      age: formData.age,
      gender: formData.gender,
      testDate: formData.testDate,
      reportDate: formData.reportDate,
      referralDoctor: formData.referralDoctor,
      reportTime: formData.reportTime,
      // X-Ray type
      XrayTypes: formData.xrayType,
      
      // Normal case
      BothNormal: formData.chestNormal,
      
      // Opacities - transform nested to flat
      opacities: formData.opacities.enabled,
      opacitiesType: formData.opacities.type,
      opacitiesRUL: formData.opacities.zones.RUZ,
      opacitiesRML: formData.opacities.zones.RMZ,
      opacitiesRLL: formData.opacities.zones.RLZ,
      opacitiesLUL: formData.opacities.zones.LUZ,
      opacitiesLML: formData.opacities.zones.LMZ,
      opacitiesLLL: formData.opacities.zones.LLZ,
      
      // Opacities findings - transform nested zoneFindings
      RuzAirBronchogram: formData.opacities.zoneFindings?.RUZ?.['Air Bronchogram'] || false,
      RuzFibrosis: formData.opacities.zoneFindings?.RUZ?.['Fibrosis'] || false,
      RuzConsolidation: formData.opacities.zoneFindings?.RUZ?.['Consolidation'] || false,
      RuzCavitation: formData.opacities.zoneFindings?.RUZ?.['Cavitation'] || false,
      
      RmzAirBronchogram: formData.opacities.zoneFindings?.RMZ?.['Air Bronchogram'] || false,
      RmzFibrosis: formData.opacities.zoneFindings?.RMZ?.['Fibrosis'] || false,
      RmzConsolidation: formData.opacities.zoneFindings?.RMZ?.['Consolidation'] || false,
      RmzCavitation: formData.opacities.zoneFindings?.RMZ?.['Cavitation'] || false,
      
      RlzAirBronchogram: formData.opacities.zoneFindings?.RLZ?.['Air Bronchogram'] || false,
      RlzFibrosis: formData.opacities.zoneFindings?.RLZ?.['Fibrosis'] || false,
      RlzConsolidation: formData.opacities.zoneFindings?.RLZ?.['Consolidation'] || false,
      RlzCavitation: formData.opacities.zoneFindings?.RLZ?.['Cavitation'] || false,
      
      LuzAirBronchogram: formData.opacities.zoneFindings?.LUZ?.['Air Bronchogram'] || false,
      LuzFibrosis: formData.opacities.zoneFindings?.LUZ?.['Fibrosis'] || false,
      LuzConsolidation: formData.opacities.zoneFindings?.LUZ?.['Consolidation'] || false,
      LuzCavitation: formData.opacities.zoneFindings?.LUZ?.['Cavitation'] || false,
      
      LmzAirBronchogram: formData.opacities.zoneFindings?.LMZ?.['Air Bronchogram'] || false,
      LmzFibrosis: formData.opacities.zoneFindings?.LMZ?.['Fibrosis'] || false,
      LmzConsolidation: formData.opacities.zoneFindings?.LMZ?.['Consolidation'] || false,
      LmzCavitation: formData.opacities.zoneFindings?.LMZ?.['Cavitation'] || false,
      
      LlzAirBronchogram: formData.opacities.zoneFindings?.LLZ?.['Air Bronchogram'] || false,
      LlzFibrosis: formData.opacities.zoneFindings?.LLZ?.['Fibrosis'] || false,
      LlzConsolidation: formData.opacities.zoneFindings?.LLZ?.['Consolidation'] || false,
      LlzCavitation: formData.opacities.zoneFindings?.LLZ?.['Cavitation'] || false,
      
      // Mild Haziness
      mildHaziness: formData.mildHaziness.enabled,
      mildHazinessRUL: formData.mildHaziness.zones.RUZ,
      mildHazinessRML: formData.mildHaziness.zones.RMZ,
      mildHazinessRLL: formData.mildHaziness.zones.RLZ,
      mildHazinessLUL: formData.mildHaziness.zones.LUZ,
      mildHazinessLML: formData.mildHaziness.zones.LMZ,
      mildHazinessLLL: formData.mildHaziness.zones.LLZ,
      mildHazinessText: formData.mildHaziness.additional,
      
      // Hilar shadows
      rightHilarShadow: formData.rightHilarShadow,
      CalcificationImpressionRHS: formData.rightHilarImpression,
      ProminentImpressionRHS: formData.rightHilarImpression,
      leftHilarShadow: formData.leftHilarShadow,
      CalcificationImpressionLHS: formData.leftHilarImpression,
      ProminentImpressionLHS: formData.leftHilarImpression,
      
      // Costophrenic angles
      rightCostophrenicAngle: formData.rightCostophrenicAngle,
      leftCostophrenicAngle: formData.leftCostophrenicAngle,
      rightObliteratedType: formData.rightPleuralEffusion,
      leftObliteratedType: formData.leftPleuralEffusion,
      
      // Pneumothorax
      pneumothorax: formData.pneumothorax,
      pneumothoraxR: formData.pneumothoraxSide === 'right',
      pneumothoraxL: formData.pneumothoraxSide === 'left',
      pneumothoraxRType: formData.pneumothoraxSeverity.right
        ? formData.pneumothoraxSeverity.right.charAt(0).toUpperCase() + formData.pneumothoraxSeverity.right.slice(1)
        : "",
      pneumothoraxLType: formData.pneumothoraxSeverity.left
        ? formData.pneumothoraxSeverity.left.charAt(0).toUpperCase() + formData.pneumothoraxSeverity.left.slice(1)
        : "",
      
      // Cardiac
      cardiacSilhouette: formData.cardiacSilhouette,
      enlargedFindings: formData.cardiacDetails,
      
      // Soft tissue
      SoftTissue: formData.softTissue,
      SoftText: formData.softTissueFindings,
      
      // Bony thoracic cage
      bonyThoracicCage: formData.bonyThoracicCage,
      ribFracture: formData.bony.ribFracture,
      ribFractureL: formData.bony.ribSide === 'left',
      ribFractureR: formData.bony.ribSide === 'right',
      RibsL: formData.bony.ribSide === 'left' ? formData.bony.ribNumbers : '',
      RibsR: formData.bony.ribSide === 'right' ? formData.bony.ribNumbers : '',
      Clavicle: formData.bony.clavicleFracture,
      ClavicleL: formData.bony.clavicleSide === 'left',
      ClavicleR: formData.bony.clavicleSide === 'right',
      ClavicleLRType: formData.bony.clavicleLocation,
      ClavicleLRType1: formData.bony.clavicleLocation,
      sternalSutures: formData.bony.sternalSutures,
      bonyinfo: formData.bony.bonyAdditional,
      
      // Trachea and mediastinal
      tracheaAndMediastinalStructures: formData.tracheaMediastinal,
      tracheaAndMediastinalStructuresShifted: formData.tracheaShiftSide,
      tracheaAndMediastinalLeftImpression: formData.tracheaImpression,
      tracheaAndMediastinalRightImpression: formData.tracheaImpression,
      
      // Diaphragm
      
      DomesOfDiaphragm: formData.domesOfDiaphragm,
      DomesLeft: formData.domesLeftEnabled,
      DomesRight: formData.domesRightEnabled,
      DomesLeftType: formData.domesLeftType,
      DomesRightType: formData.domesRightType,
      DomesLeftElevatedType: formData.domesLeftElevatedType,
      DomesRightElevatedType: formData.domesRightElevatedType,
      
      // Lines and tubes
      etLine: formData.etLine,
      cvpLine: formData.cvpLine,
      tracheostomy: formData.tracheostomy,
      NGtube: formData.ngTube,
      
      // Additional findings
      info: formData.additionalFindings,
    };
    
    return transformed;
  };

  const handleDone = (e) => {
    e.preventDefault();

    // Transform the nested form data to flat structure expected by report generator
    const flatFormData = transformFormDataForReport(formData);
    
    const reportHtml = generateXRayChestReport(flatFormData, currentUser);

    // ✅ Insert into CKEditor if available
    if (window.editor) {
      const existingData = window.editor.getData();
      const updatedData = `${existingData}<br/><hr/><br/>${reportHtml}`;
      window.editor.setData(updatedData);
    }

    // ✅ Optional callback for parent
    if (onSubmit) onSubmit(reportHtml);
    if (onClose) onClose();
    // alert("✅ X-Ray Chest report added to editor!");
  };


  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4 border rounded shadow-md max-w-4xl mx-auto">
      <h2 className="text-xl font-bold">X-RAY CHEST </h2>
       <div className="flex gap-2">
          <button
            type="submit"
            onClick={handleDone}
            className="bg-blue-500 text-white px-4 py-1 rounded"
          >
            Done
          </button>
          <button
            type="button"
            onClick={() => {
              if (onBack) onBack(); 
              if (onClose) onClose();
            }}
            className="bg-gray-500 text-white px-4 py-1 rounded"
          >
            Back
          </button>
          {/* <button
            type="button"
            onClick={() => {
              setFormData(initial);
              if (onClose) onClose();
            }}
            className="bg-red-500 text-white px-4 py-1 rounded"
          >
            Reject
          </button> */}
        </div>
      <div className="grid grid-cols-2 gap-4">
        <input className="border p-2 rounded" placeholder="Name" value={formData.name}
               onChange={e => setField('name', e.target.value)} />
        <input className="border p-2 rounded" placeholder="Patient ID" value={formData.patientId}
               onChange={e => setField('patientId', e.target.value)} />
        <input className="border p-2 rounded" placeholder="Age" type="number" value={formData.age}
               onChange={e => setField('age', e.target.value)} />
        <input className="border p-2 rounded" type="date" value={formData.testDate}
               onChange={e => setField('testDate', e.target.value)} />
        <input className="border p-2 rounded" type="date" value={formatToISODate(formData.reportDate)}
         onChange={e => setField('reportDate', formatToDDMMYYYYFromISO(e.target.value))} 
         placeholder="Report Date" />
        <div>
          <label className="font-semibold">Gender</label>
          <div className="flex gap-3 mt-1">
            {["Male","Female","Others"].map(g => (
              <label key={g} className="flex items-center gap-1">
                <input type="radio" name="gender" checked={formData.gender === g}
                       onChange={() => setField('gender', g)} /> {g}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="font-semibold text-red-600">X-Ray Type*</label>
        <div className="flex gap-4 mt-1">
          {['AP','PA','LATERAL','AP/PA'].map(t => (
            <label key={t} className="flex items-center gap-1">
              <input type="radio" name="xrayType" checked={formData.xrayType === t}
                     onChange={() => setField('xrayType', t)} /> {t}
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={formData.chestNormal} onChange={() => setField('chestNormal', !formData.chestNormal)} /> Chest Normal?
        </label>

        {/* Opacities */}
        <div className="border p-3 rounded">
        {/* Step 1: Enable Opacities */}
        <label className="flex items-center gap-2">
            <input
            type="checkbox"
            checked={formData.opacities.enabled}
            onChange={() => setField("opacities.enabled", !formData.opacities.enabled)}
            />
            Opacities?
        </label>

        {/* Step 2: If enabled, show type selection */}
        {formData.opacities.enabled && (
            <>
            <div className="mt-3">
                <div className="font-semibold">Type of Opacities</div>
                <div className="flex gap-4 mt-2">
                {["Homogeneous", "Ill-defined", "Non homogeneous"].map((opt) => (
                    <label key={opt} className="flex items-center gap-1">
                    <input
                        type="radio"
                        name="opacitiesType"
                        checked={formData.opacities.type === opt}
                        onChange={() => setField("opacities.type", opt)}
                    />
                    {opt}
                    </label>
                ))}
                </div>
            </div>

            {/* Step 3: If a type is selected, show zones */}
            {formData.opacities.type && (
                <div className="mt-3">
                <div className="font-semibold mb-1">Zones involved</div>
                <div className="grid grid-cols-3 gap-2">
                    {zones.map((z) => (
                    <div key={z} className="border rounded p-2">
                        <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={formData.opacities.zones[z]}
                            onChange={() => toggleZone("opacities", z)}
                        />
                        {z}
                        </label>

                        {/* Step 4: If this zone is selected, show 4 findings */}
                        {formData.opacities.zones[z] && (
                        <div className="ml-5 mt-2 space-y-1 text-sm">
                            {["Air Bronchogram", "Fibrosis", "Consolidation", "Cavitation"].map((finding) => (
                            <label key={finding} className="flex items-center gap-1">
                                <input
                                type="checkbox"
                                checked={formData.opacities?.zoneFindings?.[z]?.[finding] || false}
                                onChange={() =>
                                setFormData((prev) => {
                                    const copy = {
                                    ...prev,
                                    opacities: {
                                        ...prev.opacities,
                                        zoneFindings: {
                                        ...prev.opacities.zoneFindings,
                                        [z]: {
                                            ...(prev.opacities.zoneFindings?.[z] || {}),
                                            [finding]: !(
                                            prev.opacities.zoneFindings?.[z]?.[finding] || false
                                            ),
                                        },
                                        },
                                    },
                                    };
                                    return copy;
                                })
                                }

                                />
                                {finding}
                            </label>
                            ))}
                        </div>
                        )}
                    </div>
                    ))}
                </div>
                </div>
            )}
            </>
        )}
        </div>


        {/* Mild Haziness (similar) */}
        <div className="border p-3 rounded">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={formData.mildHaziness.enabled}
                     onChange={() => setField('mildHaziness.enabled', !formData.mildHaziness.enabled)} /> Mild Haziness?
            </label>
          </div>

          {formData.mildHaziness.enabled && (
            <>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {zones.map(z => (
                  <label key={z} className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.mildHaziness.zones[z]}
                           onChange={() => toggleZone('mildHaziness', z)} /> {z}
                  </label>
                ))}
              </div>
              <div className="mt-2">
                <label>Additional Findings (mild haziness)</label>
                <input className="border p-1 rounded w-full" value={formData.mildHaziness.additional}
                       onChange={e => setField('mildHaziness.additional', e.target.value)} />
              </div>
            </>
          )}
        </div>

      </div>

      {/* Hilar shadows */}
      <div className="grid grid-cols-2 gap-4">
        <div className="border p-3 rounded">
          <label className="font-semibold">Right Hilar Shadow</label>
          <div className="flex gap-3 mt-2">
            {['prominent','calcifications'].map(opt => (
              <label key={opt} className="flex items-center gap-1">
                <input type="radio" name="rightHilar" checked={formData.rightHilarShadow === opt}
                       onChange={() => setField('rightHilarShadow', opt)} /> {opt}
              </label>
            ))}
          </div>
          {['prominent','calcifications'].includes(formData.rightHilarShadow) && (
            <div className="mt-2">
              <label>Impression (Right Hilar)</label>
              <textarea className="border p-2 rounded w-full" value={formData.rightHilarImpression}
                        onChange={e => setField('rightHilarImpression', e.target.value)} />
            </div>
          )}
        </div>

        <div className="border p-3 rounded">
          <label className="font-semibold">Left Hilar Shadow</label>
          <div className="flex gap-3 mt-2">
            {['prominent','calcifications'].map(opt => (
              <label key={opt} className="flex items-center gap-1">
                <input type="radio" name="leftHilar" checked={formData.leftHilarShadow === opt}
                       onChange={() => setField('leftHilarShadow', opt)} /> {opt}
              </label>
            ))}
          </div>
          {['prominent','calcifications'].includes(formData.leftHilarShadow) && (
            <div className="mt-2">
              <label>Impression (Left Hilar)</label>
              <textarea className="border p-2 rounded w-full" value={formData.leftHilarImpression}
                        onChange={e => setField('leftHilarImpression', e.target.value)} />
            </div>
          )}
        </div>
      </div>

      {/* Costophrenic angles */}
      <div className="grid grid-cols-2 gap-4">
        <div className="border p-3 rounded">
          <label className="font-semibold">Right Costophrenic Angle</label>
          <div className="mt-2">
            <label className="flex items-center gap-2">
              <input 
                type="radio" 
                name="rightCPA" 
                checked={formData.rightCostophrenicAngle === 'obliterated'}
                onChange={() => setField('rightCostophrenicAngle', 'obliterated')} 
              /> 
              obliterated
            </label>
            {formData.rightCostophrenicAngle === 'obliterated' && (
              <label className="flex items-center gap-2 mt-2 ml-4">
                <input 
                  type="checkbox" 
                  checked={formData.rightPleuralEffusion}
                  onChange={() => setField('rightPleuralEffusion', !formData.rightPleuralEffusion)} 
                /> 
                Pleural effusion
              </label>
            )}
          </div>
        </div>

        <div className="border p-3 rounded">
          <label className="font-semibold">Left Costophrenic Angle</label>
          <div className="mt-2">
            <label className="flex items-center gap-2">
              <input 
                type="radio" 
                name="leftCPA" 
                checked={formData.leftCostophrenicAngle === 'obliterated'}
                onChange={() => setField('leftCostophrenicAngle', 'obliterated')} 
              /> 
              obliterated
            </label>
            {formData.leftCostophrenicAngle === 'obliterated' && (
              <label className="flex items-center gap-2 mt-2 ml-4">
                <input 
                  type="checkbox" 
                  checked={formData.leftPleuralEffusion}
                  onChange={() => setField('leftPleuralEffusion', !formData.leftPleuralEffusion)} 
                /> 
                Pleural effusion
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Pneumothorax */}
      <div className="border p-3 rounded">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.pneumothorax}
            onChange={() => {
              setFormData({
                ...formData,
                pneumothorax: !formData.pneumothorax,
                pneumothoraxSide: '',
                pneumothoraxSeverity: { right: '', left: '' },
              });
            }}
          />{" "}
          Pneumothorax?
        </label>

        {formData.pneumothorax && (
          <div className="mt-2 grid grid-cols-2 gap-4">
            <div>
              <div className="font-semibold">Side</div>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="pSide"
                  checked={formData.pneumothoraxSide === "right"}
                  onChange={() =>
                    setFormData({
                      ...formData,
                      pneumothoraxSide: "right",
                    })
                  }
                />{" "}
                Right
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="pSide"
                  checked={formData.pneumothoraxSide === "left"}
                  onChange={() =>
                    setFormData({
                      ...formData,
                      pneumothoraxSide: "left",
                    })
                  }
                />{" "}
                Left
              </label>
            </div>

            <div>
              <div className="font-semibold">Severity</div>
              {["mild", "moderate", "severe"].map((s) => (
                <label key={s} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`severity-${formData.pneumothoraxSide || "any"}`}
                    checked={
                      formData.pneumothoraxSeverity?.[formData.pneumothoraxSide] === s
                    }
                    onChange={() => {
                      if (!formData.pneumothoraxSide) return;
                      setFormData({
                        ...formData,
                        pneumothoraxSeverity: {
                          ...formData.pneumothoraxSeverity,
                          [formData.pneumothoraxSide]: s,
                        },
                      });
                    }}
                    disabled={!formData.pneumothoraxSide}
                  />{" "}
                  {s}
                </label>
              ))}
            </div>
          </div>
        )}
      </div>


      {/* Cardiac silhouette */}
      <div className="border p-3 rounded">
        <label className="font-semibold">Cardiac Silhouette</label>
        <div className="flex gap-4 mt-2">
          {['enlarged','cannot be commented'].map(opt => (
            <label key={opt} className="flex items-center gap-1">
              <input type="radio" name="cardiac" checked={formData.cardiacSilhouette === opt}
                     onChange={() => setField('cardiacSilhouette', opt)} /> {opt}
            </label>
          ))}
        </div>
        {formData.cardiacSilhouette === 'enlarged' && (
          <div className="mt-2">
            <label>CTR & Additional findings</label>
            <textarea className="border p-2 rounded w-full" value={formData.cardiacDetails}
                      onChange={e => setField('cardiacDetails', e.target.value)} />
          </div>
        )}
      </div>

      {/* Soft tissue */}
      <div className="border p-3 rounded">
        <label className="flex items-center gap-2"><input type="checkbox" checked={formData.softTissue} onChange={() => setField('softTissue', !formData.softTissue)} /> Soft Tissue?</label>
        {formData.softTissue && (
          <div className="mt-2">
            <label>Findings (soft tissue)</label>
            <textarea className="border p-2 rounded w-full" value={formData.softTissueFindings}
                      onChange={e => setField('softTissueFindings', e.target.value)} />
          </div>
        )}
      </div>

      {/* Bony Thoracic Cage */}
      <div className="border p-3 rounded">
        <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bonyThoracicCage} onChange={() => setField('bonyThoracicCage', !formData.bonyThoracicCage)} /> Bony Thoracic Cage?</label>

        {formData.bonyThoracicCage && (
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bony.ribFracture} onChange={() => setField('bony.ribFracture', !formData.bony.ribFracture)} /> Rib fracture</label>

              {formData.bony.ribFracture && (
                <div className="mt-2">
                  <div className="font-semibold">Side</div>
                  <label className="flex items-center gap-2"><input type="radio" name="ribSide" checked={formData.bony.ribSide === 'left'} onChange={() => setField('bony.ribSide','left')} /> Left</label>
                  <label className="flex items-center gap-2"><input type="radio" name="ribSide" checked={formData.bony.ribSide === 'right'} onChange={() => setField('bony.ribSide','right')} /> Right</label>

                  {formData.bony.ribSide && (
                    <div className="mt-2">
                      <label>Rib {formData.bony.ribSide === 'left' ? 'L' : 'R'} (numbers)</label>
                      <input className="border p-1 rounded w-full" value={formData.bony.ribNumbers}
                             onChange={e => setField('bony.ribNumbers', e.target.value)} placeholder="e.g. 4,5" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bony.clavicleFracture} onChange={() => setField('bony.clavicleFracture', !formData.bony.clavicleFracture)} /> Clavicle Fracture</label>

              {formData.bony.clavicleFracture && (
                <div className="mt-2">
                  <div className="font-semibold">Side</div>
                  <label className="flex items-center gap-2"><input type="radio" name="clavSide" checked={formData.bony.clavicleSide === 'left'} onChange={() => setField('bony.clavicleSide','left')} /> Left</label>
                  <label className="flex items-center gap-2"><input type="radio" name="clavSide" checked={formData.bony.clavicleSide === 'right'} onChange={() => setField('bony.clavicleSide','right')} /> Right</label>

                  {formData.bony.clavicleSide && (
                    <div className="mt-2">
                      <div className="font-semibold">Location</div>
                      {['proximal one third','middle one third','distal one third'].map(loc => (
                        <label key={loc} className="flex items-center gap-2">
                          <input type="radio" name={`clavLoc-${formData.bony.clavicleSide}`} checked={formData.bony.clavicleLocation === loc}
                                 onChange={() => setField('bony.clavicleLocation', loc)} /> {loc}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={formData.bony.sternalSutures} onChange={() => setField('bony.sternalSutures', !formData.bony.sternalSutures)} /> Sternal Sutures</label>
              <div className="mt-2">
                <label>Additional (bony)</label>
                <input className="border p-1 rounded w-full" value={formData.bony.bonyAdditional}
                       onChange={e => setField('bony.bonyAdditional', e.target.value)} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trachea & Mediastinal */}
      <div className="border p-3 rounded">
        <label className="font-semibold">Trachea And Mediastinal Structures?</label>
        <div className="mt-2">
          <label className="flex items-center gap-2"><input type="radio" name="trach" checked={formData.tracheaMediastinal === 'shifted'} onChange={() => setField('tracheaMediastinal', 'shifted')} /> shifted</label>
        </div>

        {formData.tracheaMediastinal === 'shifted' && (
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <div className="font-semibold">Direction</div>
              <label className="flex items-center gap-2"><input type="radio" name="trachSide" checked={formData.tracheaShiftSide === 'right'} onChange={() => setField('tracheaShiftSide','right')} /> Right</label>
              <label className="flex items-center gap-2"><input type="radio" name="trachSide" checked={formData.tracheaShiftSide === 'left'} onChange={() => setField('tracheaShiftSide','left')} /> Left</label>
            </div>
            <div>
              <label>Impression</label>
              <textarea className="border p-2 rounded w-full" value={formData.tracheaImpression}
                        onChange={e => setField('tracheaImpression', e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* Domes of diaphragm */}
      <div className="border p-3 rounded">
          <label className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={formData.domesOfDiaphragm} 
              onChange={() => {
                setField('domesOfDiaphragm', !formData.domesOfDiaphragm);
                if (formData.domesOfDiaphragm) {
                  // reset when turning off
                  setField('domesLeftEnabled', false);
                  setField('domesRightEnabled', false);
                  setField('domesLeftType', '');
                  setField('domesRightType', '');
                  setField('domesLeftElevatedType', '');
                  setField('domesRightElevatedType', '');
                }
              }}
            /> 
            Domes of Diaphragm?
          </label>

          {formData.domesOfDiaphragm && (
            <div className="mt-3 space-y-4">

              {/* LEFT SIDE */}
              <div className="border p-3 rounded">
                <label className="flex items-center gap-2 font-semibold">
                  <input
                    type="checkbox"
                    checked={formData.domesLeftEnabled}
                    onChange={() => {
                      setField('domesLeftEnabled', !formData.domesLeftEnabled);
                      setField('domesLeftType', '');
                      setField('domesLeftElevatedType', '');
                    }}
                  />
                  Left Dome
                </label>

                {formData.domesLeftEnabled && (
                  <>
                    <div className="mt-2">
                      <div className="font-medium mb-1">State</div>
                      <label className="mr-4">
                        <input
                          type="radio"
                          name="leftDomeState"
                          checked={formData.domesLeftType === 'Elevated'}
                          onChange={() => {
                            setField('domesLeftType', 'Elevated');
                            setField('domesLeftElevatedType', '');
                          }}
                        /> Elevated
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="leftDomeState"
                          checked={formData.domesLeftType === 'Flattened'}
                          onChange={() => {
                            setField('domesLeftType', 'Flattened');
                            setField('domesLeftElevatedType', '');
                          }}
                        /> Flattened
                      </label>
                    </div>

                    {formData.domesLeftType === 'Elevated' && (
                      <div className="mt-2">
                        <div className="font-medium mb-1">Reason (if elevated)</div>
                        <label className="mr-4">
                          <input
                            type="radio"
                            name="leftElevReason"
                            checked={formData.domesLeftElevatedType === 'Eventration'}
                            onChange={() => setField('domesLeftElevatedType', 'Eventration')}
                          /> Eventration
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="leftElevReason"
                            checked={formData.domesLeftElevatedType === 'Abdominal Distention'}
                            onChange={() => setField('domesLeftElevatedType', 'Abdominal Distention')}
                          /> Abdominal Distention
                        </label>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* RIGHT SIDE */}
              <div className="border p-3 rounded">
                <label className="flex items-center gap-2 font-semibold">
                  <input
                    type="checkbox"
                    checked={formData.domesRightEnabled}
                    onChange={() => {
                      setField('domesRightEnabled', !formData.domesRightEnabled);
                      setField('domesRightType', '');
                      setField('domesRightElevatedType', '');
                    }}
                  />
                  Right Dome
                </label>

                {formData.domesRightEnabled && (
                  <>
                    <div className="mt-2">
                      <div className="font-medium mb-1">State</div>
                      <label className="mr-4">
                        <input
                          type="radio"
                          name="rightDomeState"
                          checked={formData.domesRightType === 'Elevated'}
                          onChange={() => {
                            setField('domesRightType', 'Elevated');
                            setField('domesRightElevatedType', '');
                          }}
                        /> Elevated
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="rightDomeState"
                          checked={formData.domesRightType === 'Flattened'}
                          onChange={() => {
                            setField('domesRightType', 'Flattened');
                            setField('domesRightElevatedType', '');
                          }}
                        /> Flattened
                      </label>
                    </div>

                    {formData.domesRightType === 'Elevated' && (
                      <div className="mt-2">
                        <div className="font-medium mb-1">Reason (if elevated)</div>
                        <label className="mr-4">
                          <input
                            type="radio"
                            name="rightElevReason"
                            checked={formData.domesRightElevatedType === 'Eventration'}
                            onChange={() => setField('domesRightElevatedType', 'Eventration')}
                          /> Eventration
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="rightElevReason"
                            checked={formData.domesRightElevatedType === 'Abdominal Distention'}
                            onChange={() => setField('domesRightElevatedType', 'Abdominal Distention')}
                          /> Abdominal Distention
                        </label>
                      </div>
                    )}
                  </>
                )}
              </div>

            </div>
          )}
        </div>

      {/* Lines / tubes */}
      <div className="space-y-2">
        <label className="flex items-center gap-2"><input type="checkbox" checked={formData.etLine} onChange={() => setField('etLine', !formData.etLine)} /> ET line in situ?</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={formData.cvpLine} onChange={() => setField('cvpLine', !formData.cvpLine)} /> CVP line in situ?</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={formData.tracheostomy} onChange={() => setField('tracheostomy', !formData.tracheostomy)} /> Tracheostomy?</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={formData.ngTube} onChange={() => setField('ngTube', !formData.ngTube)} /> NG tube?</label>
      </div>

      <div>
        <label>Additional findings?</label>
        <textarea className="border p-2 rounded w-full" value={formData.additionalFindings}
                  onChange={e => setField('additionalFindings', e.target.value)} />
      </div>
    </form>
  );
}
